import {
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonItem,
  IonLabel,
  IonList,
  IonModal,
  IonProgressBar,
  IonTitle,
  IonToolbar,
} from '@ionic/react';
import {
  checkmarkCircle,
  close,
  closeCircle,
  warning,
} from 'ionicons/icons';
import { useTranslation } from 'react-i18next';

import { SetupCacheControls } from '../../hooks/setup-cache-controls';
import ui from '../../offline-ui.module.css';
import {
  isActiveSyncPhase,
  isTerminalSyncPhase,
} from '../../sync-job/types';

export type SyncProgressModalProps = {
  isOpen: boolean;
  onClose: () => void;
} & SetupCacheControls;

export function SyncProgressModal({
  isOpen,
  onClose,
  job,
  progress,
  isSettingUpCache,
  loadingMessage,
  pauseJob,
  resumeJob,
  cancelJob,
}: SyncProgressModalProps) {
  const { t } = useTranslation(['common'], { useSuspense: false });

  const isFinished = job ? isTerminalSyncPhase(job.status) : false;
  const isSuccess = job?.status === 'completed';
  const isPartial = job?.status === 'completed_with_errors';
  const isFailed = job?.status === 'failed';

  const plotsProgress =
    progress && progress.totalPlots > 0
      ? progress.completedPlots / progress.totalPlots
      : isFinished
        ? 1
        : 0;
  const analysesProgress =
    progress && progress.totalAnalyses > 0
      ? progress.completedAnalyses / progress.totalAnalyses
      : isFinished
        ? 1
        : 0;
  const heatmapsProgress =
    progress && progress.totalHeatmaps > 0
      ? progress.completedHeatmaps / progress.totalHeatmaps
      : isFinished
        ? 1
        : 0;

  const showHeatmaps =
    job?.options.includeHeatmaps && (progress?.totalHeatmaps ?? 0) > 0;

  const completionClass = isSuccess
    ? ui.completionSuccess
    : isPartial
      ? ui.completionWarning
      : isFailed
        ? ui.completionFailed
        : '';

  const toolbarColor = isFinished
    ? isSuccess
      ? 'success'
      : isPartial
        ? 'warning'
        : 'danger'
    : undefined;

  const modalTitle = isFinished
    ? isSuccess
      ? t('common:sync_job.completed_title')
      : isPartial
        ? t('common:sync_job.completed_partial_title')
        : t('common:sync_job.failed_title')
    : t('common:sync_job.modal_title');

  return (
    <IonModal isOpen={isOpen} onDidDismiss={onClose}>
      <IonHeader>
        <IonToolbar color={toolbarColor}>
          <IonTitle>{modalTitle}</IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={onClose}>
              <IonIcon icon={close} slot="icon-only" />
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent className={ui.page}>
        <div className="ion-padding">
          {isFinished && job ? (
            <div className={`${ui.completionPanel} ${completionClass}`}>
              <IonIcon
                className={ui.completionIcon}
                icon={
                  isSuccess
                    ? checkmarkCircle
                    : isPartial
                      ? warning
                      : closeCircle
                }
              />
              <p className={ui.completionSummary}>
                {isSuccess
                  ? t('common:sync_job.completed_summary', {
                      plots: job.totals.plots,
                      analyses: job.totals.analyses,
                    })
                  : isPartial
                    ? t('common:sync_job.completed_with_errors', {
                        count: job.errors.length,
                      })
                    : t('common:sync_job.failed')}
              </p>
            </div>
          ) : null}

          {job?.scope.managerLabel ? (
            <p className={ui.meta}>
              {t('common:sync_job.scope_label', {
                name: job.scope.managerLabel,
              })}
            </p>
          ) : null}

          {job && !isFinished ? (
            <p className={ui.mode}>
              {job.options.includeHeatmaps
                ? t('common:sync_job.mode_with_images_label')
                : t('common:sync_job.mode_without_images_label')}
            </p>
          ) : null}

          {!isFinished && loadingMessage ? (
            <p className={ui.phase}>{loadingMessage}</p>
          ) : null}

          {progress && progress.totalPlots > 0 ? (
            <div className={ui.progressPanel}>
              <span className={ui.progressLabel}>
                {t('common:sync_job.plots_line', {
                  percent: isFinished ? 100 : progress.plotsPercent,
                  done: progress.completedPlots,
                  total: progress.totalPlots,
                  remaining: isFinished ? 0 : progress.remainingPlots,
                })}
              </span>
              <IonProgressBar value={plotsProgress} />
            </div>
          ) : null}

          {progress &&
          progress.totalAnalyses > 0 &&
          (isFinished ||
            progress.phase === 'analyses' ||
            progress.phase === 'heatmaps' ||
            !isActiveSyncPhase(progress.phase)) ? (
            <div className={ui.progressPanel}>
              <span className={ui.progressLabel}>
                {t('common:sync_job.analyses_line', {
                  percent: isFinished ? 100 : progress.analysesPercent,
                  done: progress.completedAnalyses,
                  total: progress.totalAnalyses,
                  remaining: isFinished ? 0 : progress.remainingAnalyses,
                })}
              </span>
              <IonProgressBar value={analysesProgress} />
            </div>
          ) : null}

          {showHeatmaps && progress ? (
            <div className={ui.progressPanel}>
              <span className={ui.progressLabel}>
                {t('common:sync_job.heatmaps_line', {
                  percent: isFinished ? 100 : progress.heatmapsPercent,
                  done: progress.completedHeatmaps,
                  total: progress.totalHeatmaps,
                  remaining: isFinished ? 0 : progress.remainingHeatmaps,
                })}
              </span>
              <IonProgressBar value={heatmapsProgress} />
            </div>
          ) : null}

          {isPartial && job && job.errors.length > 0 ? (
            <IonList className={ui.errorsList}>
              <IonItem lines="none">
                <IonLabel>
                  <h3>{t('common:sync_job.errors_title')}</h3>
                </IonLabel>
              </IonItem>
              {job.errors.slice(-10).map((err, i) => (
                <IonItem key={`${err.entityId}-${err.at}-${i}`} lines="none">
                  <IonLabel className="ion-text-wrap">
                    <p>
                      {err.entityType} {err.entityId}
                    </p>
                    <p>{err.message}</p>
                  </IonLabel>
                </IonItem>
              ))}
            </IonList>
          ) : null}

          <div className={ui.modalActions}>
            {isFinished ? (
              <IonButton expand="block" onClick={onClose}>
                {t('common:sync_job.completed_close')}
              </IonButton>
            ) : (
              <>
                {isSettingUpCache ? (
                  <IonButton color="medium" onClick={() => pauseJob()}>
                    {t('common:sync_job.pause')}
                  </IonButton>
                ) : job?.status === 'paused' ? (
                  <IonButton onClick={() => resumeJob()}>
                    {t('common:sync_job.resume')}
                  </IonButton>
                ) : null}
                <IonButton
                  color="danger"
                  fill="outline"
                  onClick={() => cancelJob()}
                >
                  {t('common:sync_job.cancel')}
                </IonButton>
              </>
            )}
          </div>
        </div>
      </IonContent>
    </IonModal>
  );
}

export default SyncProgressModal;
